import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { eventsAPI } from "api/Interactions/EventsAPI";
import { Event } from "api/Interactions/InteractionsInterfaces";
import EventEmptyPage from "./EventsEmptyPage";
import GenericDetailPage from "components/Interactions/GenericDetailPage";
import { baseURLFrontend } from "api/APIClient";

interface EventDetailProps {
  eventId: number;
  onClose?: () => void;
}

const EventDetail: React.FC<EventDetailProps> = ({ eventId, onClose }) => {
  const theme = useTheme();
  const [record, setRecord] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const numberOfEvents = record?.people?.length || 0;
  const costPerEvent = 0.05;
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const data = await eventsAPI.getById(eventId);
        setRecord(data as Event);
      } catch (err) {
        console.error("Error loading event:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [eventId]);

  const handleUpdate = async (id: number, field: keyof Event, value: any) => {
    if (typeof id !== "number") return;
    try {
      setSaving(true);
      const updated = await eventsAPI.updateField(id, field, value);
      setRecord(updated as Event);
    } catch (err) {
      console.error(`Error updating field "${String(field)}":`, err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFulfilled = async () => {
    if (!record) return;
    await handleUpdate(record.id, "pathways_fulfilled", !record.pathways_fulfilled);
  };

  if (loading) return null;
  if (!record) return <EventEmptyPage />;


  const contentDetailFields=[
    {
      label: "Location",
      value: record.location || "",
      field: "location",
      type: "string",
    },
    {
      label: "Start Time",
      value: record.start_time || "",
      field: "start_time",
      type: "datetime",
    },
    {
      label: "End Time",
      value: record.end_time || "",
      field: "end_time",
      type: "datetime",
    },
    {
      label: "Capacity",
      value: record.capacity ?? "",
      field: "capacity",
      type: "integer",
    },
    {
      label: "Registration Required",
      value: record.registration_required ?? false,
      field: "registration_required",
      type: "boolean",
    },
    { label: "Allow team members to add people from your database", 
      value: record.external_access_to_people_database, 
      field: "external_access_to_people_database", 
      type: "boolean" 
    },

  ] as const;



  return (
    <GenericDetailPage
      record={record}
      loading={loading}
      saving={saving}
      onClose={onClose}
      onUpdate={handleUpdate}
      onToggleFulfilled={handleToggleFulfilled}
      fulfilled={false}
      fulfillmentDetailsLabel="Events"
      fulfilledByPathwaysAvailable={false}
      numberOfItems={numberOfEvents}
      costPerItem={costPerEvent}
      showSaveButton
      saveButtonColor={theme.custom.modules.interactions.primary.light}
      registration={true}
      registrationLink={baseURLFrontend + "/event/"+"registration/"+record.public_token + "/"}
      logging={true}
      loggingLink={baseURLFrontend + "/event/"+"attendance/"+record.public_token+"/"}
      contentDetailFields={contentDetailFields as any}

    />
  );
};

export default EventDetail;
